import {
  Campaign,
  ChatPlatform,
  CampaignStatus,
  CampaignMessageToken,
} from '../models';
import store from '@/store';

import dayjs from 'dayjs';
import { SubscribeQuery } from '../queries';
import ShortLinkService from './ShortLinkService';
import CampaignQueries from '@/queries/campaign.queries';
import ApiService from '@/services/ApiService';
import UtilsService from '@/services/UtilsService';

export enum CampaignTableType {
  ALL = 'ALL',
  RUNNING = 'RUNNING',
  DRAFT = 'DRAFT',
  PAUSED = 'PAUSED',
  FINISHED = 'FINISHED',
}

/** ================ BUSINESS LOGIC ======================= */

function extractDuplicatableFields(campaign: Campaign) {
  const {
    title,
    status,
    textMessage,
    platform,
    audienceType,
    audienceCount,
    audienceFile,
    audienceFileTokenMapping,
    audienceCustomerFilters,
    audienceCustomerList,
    scheduledTo,
    rateLimit,
    trackingForSales,
    routeReplies,
    smsCount,
  } = campaign;
  return {
    title,
    textMessage,
    status,
    platform,
    audienceType,
    audienceCount,
    audienceFile,
    audienceFileTokenMapping,
    audienceCustomerFilters,
    audienceCustomerList,
    scheduledTo,
    rateLimit,
    trackingForSales,
    routeReplies,
    smsCount,
  };
}

async function extractAudienceCountAndSMSCountFromFile(file: File, campaign: Campaign) {
  return new Promise((resolve, reject) => {
    const myReader = new FileReader();
    myReader.readAsText(file);
    myReader.onloadend = (e: any) => {
      try {
        resolve(extractAudienceCountAndSMSCountFromString(e.target.result, campaign));
      } catch (error) {
        reject(error);
      }
    };
  });
}

function replaceTokenFromText(textMessage: string, tokens: CampaignMessageToken[]) {
  let msg = textMessage;
  tokens.forEach((token) => {
    msg = msg.replace(new RegExp(token.name, 'g'), token.value);
  });

  return msg;
}

function extractAudienceCountAndSMSCountFromList(campaign: Campaign) {
  let smsCount = 0;
  campaign.audienceCustomerList.forEach((item) => {
    let msg = campaign.textMessage;
    item.tokens.forEach((token) => {
      msg = msg.replace(new RegExp(token.name, 'g'), token.value);
    });
    const numberOfMessages = CampaignService.extractTextToMessages(msg).length;
    smsCount += numberOfMessages;
  });
  return {
    audienceCount: campaign.audienceCustomerList.length,
    smsCount,
  };

}

function extractAudienceCountAndSMSCountFromString(csvString: string, campaign: Campaign) {
  let lines: string[] = [];
  if (csvString.includes('\r\n')) {
    lines = csvString.split('\r\n').filter((value) => !!value);
  } else if (csvString.includes('\n')) {
    lines = csvString.split('\n').filter((value) => !!value);
  } else if (csvString.includes('\r')) {
    lines = csvString.split('\r').filter((value) => !!value);
  }
  const audienceCount = lines.length - 1;
  const columns: string[] = lines[0].split(',').map((col) => col.trim());
  lines.splice(0, 1);
  let smsCount = 0;
  const messageTokens = extractTokensFromText(campaign.textMessage);
  lines.forEach((line: string) => {
    const cells = line.split(',').map((col) => col.trim());
    let messageRow = campaign.textMessage;
    messageTokens.forEach((token) => {
      const found = campaign.audienceFileTokenMapping.find((t) => t.token === token);
      const columnName = found && found.fileColumn;
      const colIndex = columns.findIndex((col) => col === columnName);
      if (colIndex === -1) {
        throw new Error(`coluna ${columnName} não existe no arquivo csv`);
      }
      messageRow = messageRow.replace(new RegExp(token, 'g'), cells[colIndex]);
    });
    smsCount += extractTextToMessages(messageRow).length;
  });
  return {
    audienceCount, smsCount,
  };
}

function extractTextToMessages(text: string): string[] {
  const messages = [];
  if (!text) { return ['']; }
  messages.push(text);
  return messages;
}

function extractTokensFromText(text: string) {
  return text && text.match(/{{.*?}}/g) || [];
}

function buildAudienceFileTokenMapping(textMessage: string) {
  const tokenOptions = extractTokensFromText(textMessage);
  return tokenOptions.map((token) => ({
    fileColumn: '',
    token,
  }));
}

function isRunning(campaign: Campaign) {
  if (campaign.scheduledTo && campaign.scheduledTo.iso) {
    const date = dayjs(campaign.scheduledTo.iso);
    return dayjs().isAfter(date);
  }
  return false;
}

/** ================ REQUESTS ======================= */
async function loadCampaignCounters() {
  // @TODO create a cloud function to do this
  const allPromise = getAllCampaignsByType(0, 0, undefined, undefined, CampaignTableType.ALL).then((res) => (
    res.count
  ));
  const draftPromise = getAllCampaignsByType(0, 0, undefined, undefined, CampaignTableType.DRAFT).then((res) => (
    res.count
  ));
  const finishedPromise = getAllCampaignsByType(0, 0, undefined, undefined, CampaignTableType.FINISHED).then((res) => (
    res.count
  ));
  const runningPromise = getAllCampaignsByType(0, 0, undefined, undefined, CampaignTableType.RUNNING).then((res) => (
    res.count
  ));
  const pausedPromise = getAllCampaignsByType(0, 0, undefined, undefined, CampaignTableType.PAUSED).then((res) => (
    res.count
  ));
  const results = await Promise.all([
    allPromise,
    draftPromise,
    finishedPromise,
    runningPromise,
    pausedPromise,
  ]);
  return {
    [CampaignTableType.ALL]: results[0],
    [CampaignTableType.DRAFT]: results[1],
    [CampaignTableType.FINISHED]: results[2],
    [CampaignTableType.RUNNING]: results[3],
    [CampaignTableType.PAUSED]: results[4],
  };
}

function getAllCampaignsByType(
  page = 1,
  pageSize = 10,
  orderBy = '',
  orderDesc = false,
  type: CampaignTableType = CampaignTableType.ALL,
) {
  let query = {};
  let func = 'getAll';
  if (type === CampaignTableType.DRAFT) {
    func = 'getAllDraft';
  } else if (type === CampaignTableType.FINISHED) {
    func = 'getAllFinished';
  } else if (type === CampaignTableType.PAUSED) {
    func = 'getAllPaused';
  } else if (type === CampaignTableType.RUNNING) {
    func = 'getAllRunning';
  }
  query = (CampaignQueries as any)[func](
    store.state.auth.currentUser.retailer.objectId,
    page,
    pageSize,
    orderBy,
    orderDesc,
  );
  return ApiService.get(UtilsService.buildFullPath(query as SubscribeQuery));
}

function loadCampaignById(id: string) {
  const query = CampaignQueries.getById(id);
  return ApiService.get(UtilsService.buildFullPath(query)).then(
    (res) => res.results && res.results.length && res.results[0],
  );
}

/**
 * Creates an empty campaign draft or a draft from the provided campaign
 *
 * @param campaign campaign to be duplicated
 */
function createCampaignDraft(campaign?: Campaign) {
  const postData: any = {
    ...campaign && extractDuplicatableFields(campaign) || {},
  };
  postData.retailer = {
    __type: 'Pointer',
    className: 'Retailer',
    objectId: store.state.auth.currentUser.retailer.objectId,
  } as any;
  postData.status = CampaignStatus.Draft;
  postData.platform = ChatPlatform.Sms;
  postData.trackingForSales = true;
  postData.createdBy = {
    __type: 'Pointer',
    className: '_User',
    objectId: store.state.auth.currentUser.objectId,
  };
  return ApiService.post('/parse/classes/Campaign', postData)
    .then(async (res) => {
      if (campaign) {
        // update the new campaign with the links from the older
        const links = await ShortLinkService.getAllShortLink(campaign.objectId as string);
        for (const link of links) {
          const result: any = await ShortLinkService.createURLShortnerToCampaign(link.destinationUrl, res.objectId);
          postData.textMessage = postData.textMessage
            .replace(new RegExp(link.objectId as string, 'gi'), result.objectId);
        }
        await updateCampaign(res.objectId, { textMessage: postData.textMessage });
      }
      return {
        ...postData, ...res,
      };
    });
}

function createCampaign(campaignData: Campaign) {
  const postData: any = extractDuplicatableFields(campaignData);
  postData.retailer = {
    __type: 'Pointer',
    className: 'Retailer',
    objectId: store.state.auth.currentUser.retailer.objectId,
  } as any;
  postData.createdBy = {
    __type: 'Pointer',
    className: '_User',
    objectId: store.state.auth.currentUser.objectId,
  };
  postData.platform = ChatPlatform.Sms;
  postData.trackingForSales = true;
  return ApiService.post('/parse/classes/Campaign', postData)
    .then((res) => ({ ...postData, ...res }));
}

function updateCampaign(campaignId: string, campaignData: any) {
  const newData = extractDuplicatableFields(campaignData);
  return ApiService.put(`/parse/classes/Campaign/${campaignId}`, newData);
}

function deleteCampaign(campaignId: string) {
  return ApiService.del(`/parse/classes/Campaign`, campaignId);
}

function uploadCampaignAudience(campaignAudienceFile: File) {
  return ApiService.postWithFile(`/parse/files/${campaignAudienceFile.name}`, campaignAudienceFile);
}

const CampaignService = {
  loadCampaignCounters,
  getAllCampaignsByType,
  createCampaign,
  createCampaignDraft,
  updateCampaign,
  uploadCampaignAudience,
  deleteCampaign,
  loadCampaignById,
  buildAudienceFileTokenMapping,
  extractTextToMessages,
  extractTokensFromText,
  replaceTokenFromText,
  extractAudienceCountAndSMSCountFromFile,
  extractAudienceCountAndSMSCountFromString,
  extractAudienceCountAndSMSCountFromList,
  extractDuplicatableFields,
  isRunning,
};

export default CampaignService;
